import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, BadgeCheck, Award, X } from "lucide-react";
import { api } from "../lib/api";

const BADGES = [
  { icon: BadgeCheck, text: "IGI Certified" },
  { icon: Award, text: "Lifetime Warranty" },
  { icon: ShieldCheck, text: "Secure Checkout" },
];

export const PriceMatch = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", competitor_url: "", product_link: "", notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.competitor_url) {
      toast("Please add your name, email and the competitor link.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/quotes", form);
      toast("Quote request received. our team will reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", competitor_url: "", product_link: "", notes: "" });
      setOpen(false);
    } catch {
      toast("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <section data-testid="price-match-section" className="bg-[#FAF7F1] border-y border-[#EFE7D8]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto text-center"
        >
          <p className="label-track text-[#C8A165] mb-4">Price Match Guarantee</p>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] leading-[1.05]">
            Found It Somewhere Else?
          </h2>
          <p className="mt-6 text-[#525252] leading-relaxed max-w-2xl mx-auto">
            Share a product link from another retailer and we'll beat their price by at least{" "}
            <span className="text-[#0A0A0A] font-medium">10% on the same product</span>. of equal or better quality.
          </p>
          <div className="mt-10 flex flex-col items-center gap-8">
            <button
              data-testid="price-match-open-btn"
              onClick={() => setOpen(true)}
              className="bg-[#0A0A0A] text-white px-10 py-4 label-track transition-all duration-300 hover:bg-[#C8A165] hover:shadow-[0_10px_30px_-10px_rgba(200,161,101,0.6)]"
            >
              Get My Quote
            </button>
            <div className="flex items-center gap-8 flex-wrap justify-center">
              {BADGES.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-[#525252]">
                  <Icon size={16} strokeWidth={1.4} className="text-[#C8A165]" />
                  <span className="label-track text-[10px]">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal */}
      {open && (
        <div
          data-testid="price-match-modal"
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full max-w-lg p-8 md:p-10 relative border-t-2 border-[#C8A165]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-[#737373] hover:text-[#0A0A0A]"
              data-testid="price-match-close"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <p className="label-track text-[#C8A165] mb-3">Price Match</p>
            <h3 className="font-heading text-3xl text-[#0A0A0A] mb-2">Request a Quote</h3>
            <p className="text-[#737373] text-sm mb-8">We'll reply within 24 hours with our beat-it offer.</p>
            <form onSubmit={submit} className="space-y-5">
              {[
                { k: "name", label: "Full Name", type: "text", required: true },
                { k: "email", label: "Email", type: "email", required: true },
                { k: "phone", label: "Phone (optional)", type: "tel", required: false },
                { k: "competitor_url", label: "Competitor Product Link", type: "url", required: true },
                { k: "product_link", label: "Dreamonds Match (optional)", type: "url", required: false },
              ].map((f) => (
                <div key={f.k}>
                  <label className="label-track text-[#737373] block mb-2">{f.label}</label>
                  <input
                    data-testid={`quote-${f.k}`}
                    type={f.type}
                    required={f.required}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="w-full bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] py-2 outline-none text-[#0A0A0A] transition-colors"
                  />
                </div>
              ))}
              <div>
                <label className="label-track text-[#737373] block mb-2">Notes</label>
                <textarea
                  data-testid="quote-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-transparent border-b border-[#E5E5E5] focus:border-[#0A0A0A] py-2 outline-none text-[#0A0A0A] resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="quote-submit"
                className="w-full bg-[#0A0A0A] text-white py-4 label-track hover:bg-[#C8A165] transition-colors disabled:opacity-50 mt-4"
              >
                {busy ? "Sending…" : "Submit Quote Request"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};
